/**
 * @file CancelItemModal.tsx
 * @description Modal dialog enforcing reason selection for items cancelled after kitchen dispatch.
 */

import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface CancelItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  onConfirmCancel: (reason: string) => Promise<void>;
}

const CANCEL_REASONS = [
  'Customer changed mind',
  'Mistake / wrong item',
  'Food taking too long',
  'Allergy concern',
  'Kitchen out of stock',
];

export const CancelItemModal: React.FC<CancelItemModalProps> = ({
  isOpen,
  onClose,
  itemName,
  onConfirmCancel,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>(CANCEL_REASONS[0]);
  const [customReason, setCustomReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const finalReason = customReason.trim() ? customReason.trim() : selectedReason;
    try {
      setIsSubmitting(true);
      await onConfirmCancel(finalReason);
      onClose();
    } catch (err) {
      console.error('Cancellation error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-3xl p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-rose-400">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <h3 className="text-base font-black uppercase text-white">Cancel Kitchen Item</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-zinc-400 mb-4">
          <span className="font-bold text-zinc-200">{itemName}</span> has already been sent to the kitchen.
          A cancellation reason is required for the kitchen alert log.
        </p>

        <div className="space-y-2 mb-4">
          {CANCEL_REASONS.map(reason => (
            <button
              key={reason}
              type="button"
              onClick={() => {
                setSelectedReason(reason);
                setCustomReason('');
              }}
              className={`w-full p-3 rounded-xl text-xs font-semibold text-left border transition-all ${
                selectedReason === reason && !customReason
                  ? 'bg-rose-950/40 text-rose-300 border-rose-600/80 font-bold'
                  : 'bg-zinc-900 text-zinc-400 border-zinc-800'
              }`}
            >
              {reason}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Or enter custom reason..."
            value={customReason}
            onChange={e => setCustomReason(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-900 border border-zinc-800 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-rose-500"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-zinc-900 text-zinc-400 text-xs font-bold hover:text-white"
          >
            Go Back
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleConfirm}
            className="flex-1 py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-black uppercase tracking-wider disabled:opacity-50"
          >
            {isSubmitting ? 'Cancelling...' : 'Confirm Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};
